import { useDarkMode } from "@/hooks/useDarkMode";
import { CheckCircle, Info, Trash2, TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shadcn/components/ui/dialog";
import { StandardButton } from "@/components/common/StandardButton";

interface ModalProps {
  isModalOpen: boolean;
  title: string;
  subtitle: string;
  cancelButtonTitle: string;
  actionButtonTitle: string;
  actionButtonType: "default" | "error" | "warning";
  type: "default" | "error" | "success" | "warning";
  onClose;
  onClick;
  maxWidth?: string; // Optional max width, defaults to"600px"
}

function ModalWithAction(props: ModalProps) {
  const {
    isModalOpen,
    title,
    subtitle,
    cancelButtonTitle,
    actionButtonTitle,
    onClose,
    onClick,
    maxWidth = "600px",
  } = props;
  const isDark = useDarkMode();

  let icon;
  let borderColor;

  switch (props.type) {
    case "success":
      borderColor = "#10B981"; // Green color for success
      icon = <CheckCircle size={24} color={isDark ? "#6EE7B7" : borderColor} />;
      break;
    case "error":
      borderColor = "#F87171"; // Red color for error
      icon = <Trash2 size={24} color={isDark ? "#6EE7B7" : borderColor} />;
      break;
    case "warning":
      borderColor = "#F87171"; // Red color for error
      icon = (
        <TriangleAlert size={24} color={isDark ? "#6EE7B7" : borderColor} />
      );
      break;
    default:
      borderColor = "#352FC3"; // Blue color for default
      icon = <Info size={24} color={isDark ? "#A39FE8" : borderColor} />;
      if (isDark) {
        borderColor = "#A39FE8";
      }
      break;
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={onClose}>
      <DialogContent
        style={{
          borderTopColor: borderColor,
          borderTopWidth: "10px",
          maxWidth: maxWidth,
        }}
        className="w-[80%] max-h-[90vh] bg-surface-primary rounded-[10px] px-[16px] py-[12px] flex flex-col overflow-y-auto overflow-x-hidden [&>button]:hidden"
      >
        <DialogHeader className="flex flex-row items-start space-y-0 text-left">
          <div className="w-8 h-8 flex-shrink-0">{icon}</div>
          <DialogTitle className="title-s-semibold flex-1 text-left text-fg-neutral-strong">
            {title}
          </DialogTitle>
        </DialogHeader>

        <DialogDescription
          id="subtitle"
          className="label-m-medium text-fg-neutral-strong break-words py-4 text-left"
        >
          {subtitle}
        </DialogDescription>

        <DialogFooter className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 gap-2">
          <StandardButton
            variant={
              props.actionButtonType === "error" ||
              props.actionButtonType === "warning"
                ? "destructive"
                : "primary"
            }
            size="sm"
            className="sm:w-[calc(50%-4px)] order-2 sm:order-2"
            onClick={onClick}
          >
            {actionButtonTitle}
          </StandardButton>
          {cancelButtonTitle != "" && (
            <StandardButton
              variant="secondary"
              size="sm"
              className="sm:w-[calc(50%-4px)] order-1 sm:order-1"
              onClick={onClose}
            >
              {cancelButtonTitle}
            </StandardButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalWithAction;
